// Copyright (C) 2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CombinedState } from 'reducers/interfaces';
import notification from 'antd/lib/notification';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import Tabs from 'antd/lib/tabs';
import { LaptopOutlined, PlayCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { setSettings } from '../../actions/settings-actions';
import PlayerSettingsContainer from '../../containers/header/settings-modal/player-settings';
import WorkspaceSettingsContainer from '../../containers/header/settings-modal/workspace-settings';

export default function SettingsPage(): JSX.Element {
    const settings = useSelector((state: CombinedState) => state.settings);
    const dispatch = useDispatch();

    const onSaveSettings = (): void => {
        const settingsForSaving: any = {};
        for (const [key, value] of Object.entries(settings)) {
            if (typeof value === 'object') {
                settingsForSaving[key] = value;
            }
        }
        localStorage.setItem('clientSettings', JSON.stringify(settingsForSaving));
        notification.success({
            message: 'Settings was successfully saved',
            className: 'cvat-notification-notice-save-settings-success',
        });
    };

    useEffect(() => {
        try {
            const newSettings: any = {};
            const settingsString = localStorage.getItem('clientSettings') as string;
            if (!settingsString) return;
            const loadedSettings = JSON.parse(settingsString);
            for (const [sectionKey, section] of Object.entries(settings)) {
                for (const [key, value] of Object.entries(section)) {
                    let settedValue = value;
                    if (sectionKey in loadedSettings && key in loadedSettings[sectionKey]) {
                        settedValue = loadedSettings[sectionKey][key];
                    }
                    if (!(sectionKey in newSettings)) newSettings[sectionKey] = {};
                    newSettings[sectionKey][key] = settedValue;
                }
            }
            dispatch(setSettings(newSettings));
        } catch {
            notification.error({
                message: 'Failed to load settings from local storage',
                className: 'cvat-notification-notice-load-settings-fail',
            });
        }
    }, []);

    return (
        <div className='cvat-settings-tabs'>
            <Tabs type='card' tabBarStyle={{ marginBottom: '0px', marginLeft: '-1px' }}>
                <Tabs.TabPane
                    tab={(
                        <span>
                            <PlayCircleOutlined />
                            <Text>Player</Text>
                        </span>
                    )}
                    key='player'
                >
                    <PlayerSettingsContainer />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={(
                        <span>
                            <LaptopOutlined />
                            <Text>Workspace</Text>
                        </span>
                    )}
                    key='workspace'
                >
                    <WorkspaceSettingsContainer />
                </Tabs.TabPane>
            </Tabs>
            <Tooltip title='Will save settings from this page and appearance settings on standard workspace page in browser'>
                <Button type='primary' onClick={onSaveSettings}>
                    Save
                </Button>
            </Tooltip>
        </div>
    );
}
